import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from "@/components/common";
import { NewJobDialog } from "@/modules/interventions/NewJobDialog";
import { useGarage } from "@/context/GarageContext";
import { useAuth } from "@/context/AuthContext";
import { QuickChips, ViewSwitcher } from "@/components/layout/Record";
import { useFetch } from "@/lib/useFetch";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

const FILTERS = ["active", "all", "received", "inspecting", "awaiting_approval", "approved", "in_progress", "ready", "completed", "cancelled"];

export default function Jobs() {
  const { garage, readOnly, access } = useGarage();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "active";
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");
  const navigate = useNavigate();
  const scope = { vehicle_id: params.get("vehicle_id") || "", customer_id: params.get("customer_id") || "" };
  const { data: raw, error, loading, reload } = useFetch("/jobs", { status: status === "all" ? FILTERS.slice(2).join(",") : status, q, ...scope });
  const { user } = useAuth();
  const [quick, setQuick] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const data = raw && raw.filter((j) => (quick !== "mine" || j.mechanic_id === user.id) && (quick !== "today" || (j.promised_at || "").slice(0, 10) === today));
  return (
    <div>
      <PageHeader title={t("jobs.title")} subtitle={access.role === "mechanic" ? t("jobs.subtitleMechanic") : t("jobs.subtitle")}
        actions={!readOnly && <Button onClick={() => setOpen(true)} data-testid="new-job-button"><Plus className="mr-2 h-4 w-4" /> {t("jobs.new")}</Button>} />
      <div className="mb-5 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <ViewSwitcher current="list" views={[["list"], ["kanban", "/pro/board"]]} />
          <QuickChips testPrefix="jobs-quick" value={quick} onChange={(v) => setQuick(quick === v ? "" : v)} options={[["mine", t("b13.chips.myJobs")], ["today", t("b13.chips.dueToday")]]} />
          {(scope.vehicle_id || scope.customer_id) && <button type="button" onClick={() => setParams({ status })} className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="jobs-clear-scope">{t("b13.filtered")} ×</button>}
          <div className="relative w-full sm:ml-auto sm:w-64">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("jobs.search")} className="pl-9" data-testid="jobs-search-input" />
          </div>
        </div>
        <QuickChips testPrefix="jobs-filter" value={status} onChange={(s) => setParams({ status: s, ...Object.fromEntries(Object.entries(scope).filter(([, v]) => v)) })}
          options={FILTERS.map((s) => [s, s === "active" ? t("jobs.filterActive") : s === "all" ? t("quotes.all") : t(`status.${s}`)])} />
      </div>
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.length === 0 && <EmptyState icon={Wrench} title={t("jobs.empty")} text={t("jobs.emptyText")} testId="jobs-empty" />}
      {data?.length > 0 && (
        <div className="gf-card divide-y divide-slate-100 overflow-hidden" data-testid="jobs-list">
          {data.map((j) => (
            <button key={j.id} type="button" onClick={() => navigate(`/pro/jobs/${j.id}`)} data-testid={`job-row-${j.number}`}
              className="grid w-full grid-cols-2 items-center gap-3 px-5 py-4 text-left transition-colors duration-150 hover:bg-slate-50 md:grid-cols-[110px_1.2fr_1fr_1.5fr_150px]">
              <span className="font-mono text-sm font-semibold">{j.number}</span>
              <span className="min-w-0"><span className="font-mono-plate font-semibold">{j.vehicle.plate}</span> <span className="text-sm text-slate-600">{j.vehicle.make} {j.vehicle.model}</span></span>
              <span className="truncate text-sm">{j.customer.name}{j.mechanic_name && <span className="block text-xs text-slate-500">{j.mechanic_name}</span>}</span>
              <span className="hidden truncate text-sm text-slate-600 md:block">{j.reason}</span>
              <span className="flex flex-col items-start gap-1 md:items-end"><StatusBadge status={j.status} /><span className="text-[11px] text-slate-400">{fmtDateTime(j.updated_at, garage.timezone)}</span></span>
            </button>
          ))}
        </div>
      )}
      {open && <NewJobDialog open={open} onOpenChange={setOpen} onCreated={(j) => navigate(`/pro/jobs/${j.id}`)} />}
    </div>
  );
}
